import React from 'react';
import { X, Check, Sparkles, ArrowRight, Phone, Users, Bell, Clock } from 'lucide-react';

/**
 * Feature Showcase Modal
 * Opens when user clicks "Explore Features" on a locked module
 * or "Preview Features" on a coming soon module
 * Displays full value proposition with benefits and upgrade CTA
 */
const FeatureShowcaseModal = ({ isOpen, onClose, module }) => {
  if (!isOpen || !module) return null;

  const Icon = module.icon;
  const valueProps = module.valueProposition;
  const isComingSoon = module.isComingSoon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Header - Fixed */}
        <div className={`relative p-5 rounded-t-xl flex-shrink-0 ${isComingSoon ? 'bg-gradient-to-br from-purple-600 to-purple-700' : 'bg-gradient-to-br from-teal-600 to-teal-700'}`}>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={16} className="text-white" />
          </button>

          {/* Coming Soon Badge */}
          {isComingSoon && (
            <div className="absolute top-3 left-5 flex items-center gap-1 bg-white/20 text-white text-xs font-medium px-2 py-1 rounded-full">
              <Clock size={12} />
              <span>Coming Soon</span>
            </div>
          )}

          {/* Module Info */}
          <div className={`flex items-center gap-4 ${isComingSoon ? 'mt-6' : ''}`}>
            <div className="w-14 h-14 rounded-lg bg-white/20 flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{module.name}</h2>
              <p className={`text-sm ${isComingSoon ? 'text-purple-100' : 'text-teal-100'}`}>{module.description}</p>
            </div>
          </div>

          {/* Social Proof Badge - only for locked modules, not coming soon */}
          {!isComingSoon && (
            <div className="flex items-center gap-1.5 mt-4 text-teal-100 text-xs">
              <Users size={12} />
              <span>Trusted by <strong className="text-white">200+ restaurants</strong> across India</span>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Value Stat - Hero */}
          {valueProps && (
            <div className={`text-center py-4 border rounded-lg ${isComingSoon ? 'border-purple-100 bg-purple-50/50' : 'border-teal-100 bg-teal-50/50'}`}>
              <span className={`text-3xl font-bold ${isComingSoon ? 'text-purple-600' : 'text-teal-600'}`}>
                {valueProps.stat}
              </span>
              <p className="text-gray-600 text-sm mt-1">{valueProps.description}</p>
            </div>
          )}

          {/* Benefits List */}
          {valueProps?.benefits && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {isComingSoon ? "What's Coming" : "What's Included"}
              </h3>
              <div className="space-y-2">
                {valueProps.benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50"
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isComingSoon ? 'bg-purple-500' : 'bg-teal-500'}`}>
                      <Check size={12} className="text-white" strokeWidth={3} />
                    </div>
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feature Tags */}
          <div className="flex flex-wrap gap-2">
            {module.features.map((feature, idx) => (
              <span
                key={idx}
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${isComingSoon ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}
              >
                {feature}
              </span>
            ))}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 p-5 pt-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          {isComingSoon ? (
            <>
              {/* Coming Soon - Notify Me CTA */}
              <button
                className="w-full py-3 px-4 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                onClick={() => {
                  // Future: add to notification list
                  onClose();
                }}
              >
                <Bell size={16} />
                Notify Me When Available
              </button>

              {/* Info text */}
              <p className="text-center text-xs text-gray-500 mt-3">
                We'll send you an email when this feature is ready to use.
              </p>
            </>
          ) : (
            <>
              {/* Primary CTA - Free Trial */}
              <button
                className="w-full py-3 px-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                onClick={() => {
                  // Future: navigate to trial signup
                  onClose();
                }}
              >
                <Sparkles size={16} />
                Start 14-Day Free Trial
              </button>

              {/* Secondary CTA */}
              <button
                className="w-full py-2.5 px-4 mt-2 text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-colors flex items-center justify-center gap-2 text-sm"
                onClick={() => {
                  // Future: navigate to pricing page
                  onClose();
                }}
              >
                View All Plans
                <ArrowRight size={14} />
              </button>

              {/* Contact sales */}
              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500">
                <span>Questions?</span>
                <a href="tel:+919876543210" className="flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium transition-colors">
                  <Phone size={12} />
                  Contact Sales
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureShowcaseModal;
